import { useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import type { IDurationData } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { monthToDateTracking } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    useGranularities,
    useMTD,
} from "../../../../components/ChannelAnalysisChart/ChannelAnalysisChartContainerHooks";
import {
    dataModeMap,
    numbersDataFormat,
    percentDataFormat,
} from "../../../../components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { indexToDataFormat } from "../../../consts";
import useSubtitleFilters from "../../../hooks/useSubtitleFilters";
import { getDateRange, isPeriodOverPeriod, updateUrlParam } from "../utils";

const useBetaChannelTrafficConfig = (durationObject: IDurationData) => {
    const { params } = useCurrentStateAndParams();
    const { channelAnalysisMtd, channelAnalysisGranularity } = params;

    const periodDuration = useMemo(
        () => durationObject.raw.to.diff(durationObject.raw.from, "months"),
        [durationObject],
    );
    const component = swSettings.current;
    const newMMXAlgoStartData = component.resources.NewAlgoMMX;

    const { isMTDActive, isMTDSupported, setMTD } = useMTD({
        isWindow,
        disableMTD: false,
        isPeriodOverPeriod,
        durationObject,
        component,
        urlMtdParam: channelAnalysisMtd ? JSON.parse(channelAnalysisMtd) : null,
    });

    const subtitleFilters = useSubtitleFilters(isMTDActive);

    const { selectedGranularity, availableGranularities, updateGranularity } = useGranularities({
        isWindow,
        isPeriodOverPeriod,
        periodDuration,
        urlGranularityParam: channelAnalysisGranularity,
        updateUrlParam,
    });

    const [selectedVisualizationIndex, setSelectedVisualizationIndex] = useState(1);
    const [startDate, endDate] = getDateRange(durationObject, isMTDActive);

    const handleGranularityChange = (index: number) => {
        const nextGranularity = availableGranularities.find(({ index: i }) => i === index)?.value;
        if (!nextGranularity) {
            return;
        }

        proNavigator.updateParams({ channelAnalysisGranularity: nextGranularity });
        updateGranularity(nextGranularity);
        allTrackers.dispatchTrackEvent(
            "Time Frame Button",
            "switch",
            `Over Time Graph/TrafficShare/${nextGranularity}`,
        );
    };
    const isShowMMXAlertBell = () =>
        startDate.isBefore(dayjs(newMMXAlgoStartData)) && endDate.isAfter(dayjs(newMMXAlgoStartData));
    function isWindow() {
        return durationObject.forAPI.isWindow;
    }
    const handleToggleMTD = () => {
        const nextMTD = !isMTDActive;
        proNavigator.updateParams({ channelAnalysisMtd: nextMTD });
        setMTD();
        monthToDateTracking()(nextMTD);
    };

    const handleViewModeChange = (index: number) => {
        const format = indexToDataFormat[index];
        setSelectedVisualizationIndex(index);
        allTrackers.dispatchTrackEvent("Measure Button", "click", `TrafficShare/${format}`);
    };

    const switcherViewModeConfig = useMemo(
        () => ({
            items: [
                { title: percentDataFormat, value: dataModeMap[percentDataFormat] },
                { title: numbersDataFormat, value: dataModeMap[numbersDataFormat] },
            ],
            onItemClick: handleViewModeChange,
            initialSelectedItem: selectedVisualizationIndex,
        }),
        [selectedVisualizationIndex],
    );

    const switcherGranularitiesConfig = useMemo(
        () => ({
            items: availableGranularities,
            onItemClick: handleGranularityChange,
            initialSelectedItem: selectedGranularity.index,
        }),
        [availableGranularities, selectedGranularity],
    );

    const mtdOnOffConfig = useMemo(
        () => [
            {
                onClick: handleToggleMTD,
                isDisabled: !isMTDSupported,
                isSelected: isMTDActive,
                onLabel: translate("wa.mmx.channel_analysis.mtd.toggle_label"),
            },
        ],
        [isMTDSupported, isMTDActive],
    );

    return {
        selectedGranularity,
        switcherViewModeConfig,
        mtdOnOffConfig,
        switcherGranularitiesConfig,
        selectedVisualizationIndex,
        setSelectedVisualizationIndex,
        isMTDActive,
        startDate,
        endDate,
        subtitleFilters,
        isShowMMXAlertBell,
    };
};

export default useBetaChannelTrafficConfig;
